import { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { api } from '../api/client';
import { useT } from '../i18n';
import { SCORE_DIMENSIONS, type DimLang } from '../../shared/scoreDimensions';
import { BarList, Dots, ForceGraph, Heatmap, Kpi, Legend, Lines, Spark, StackedColumns, pct } from '../components/charts';

type Tab = 'overview' | 'together' | 'journeys' | 'profiles' | 'observations' | 'themes' | 'adherence' | 'quality';
const TABS: Tab[] = ['overview', 'together', 'journeys', 'profiles', 'observations', 'themes', 'adherence', 'quality'];
const PERIODS: { key: string; days: string }[] = [{ key: 'all', days: 'all' }, { key: '365', days: '365' }, { key: '180', days: '180' }, { key: '90', days: '90' }];

function useInsight<T>(path: string, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let alive = true;
    setData(null); setError(null);
    api.get<T>(path).then((d) => { if (alive) setData(d); }).catch((e) => { if (alive) setError(String(e)); });
    return () => { alive = false; };
  }, [...deps, tick]);  // eslint-disable-line react-hooks/exhaustive-deps
  return { data, error, reload: () => setTick((t) => t + 1) };
}

const DIM_COLORS = ['var(--ink-1)', 'var(--accent)', 'var(--warn)', 'var(--err)', 'var(--ink-3)'];
const LETTERS = ['A', 'B', 'C', 'D', 'E'];

/**
 * The Observatoire (admin): what the whole corpus says — which themes, together,
 * in what order, for whom, with what movement — and who records what.
 */
export function InsightsPage() {
  const { t } = useT();
  const [tab, setTab] = useState<Tab>(() => (localStorage.getItem('evolution.insights.tab') as Tab) || 'overview');
  const [period, setPeriod] = useState<string>(() => localStorage.getItem('evolution.insights.period') || 'all');
  useEffect(() => { localStorage.setItem('evolution.insights.tab', tab); }, [tab]);
  useEffect(() => { localStorage.setItem('evolution.insights.period', period); }, [period]);
  const days = period === 'all' ? '' : `days=${period}`;

  return (
    <div className="insights">
      <div className="page-header">
        <div>
          <h1 style={{ margin: 0 }}>{t('obs.title')}</h1>
          <div className="page-header-meta">{t('obs.intro')}</div>
        </div>
        {tab !== 'adherence' && tab !== 'themes' && (
          <div className="scope-toggle" role="radiogroup" aria-label={t('obs.period')}>
            {PERIODS.map((p) => (
              <button key={p.key} type="button" role="radio" aria-checked={period === p.key} className={`scope-toggle-btn ${period === p.key ? 'scope-toggle-btn--active' : ''}`} onClick={() => setPeriod(p.key)}>
                {t(`obs.period.${p.key}`)}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="tabs insights-tabs">
        {TABS.map((k) => (
          <button key={k} className={`tab ${tab === k ? 'tab--active' : ''}`} onClick={() => setTab(k)}>{k==='quality'?'Qualité des données':t(`obs.tab.${k}`)}</button>
        ))}
      </div>
      {tab === 'quality' && <QualityReport days={days} />}
      {tab === 'overview' && <Overview days={days} />}
      {tab === 'together' && <Together days={days} />}
      {tab === 'journeys' && <Journeys days={days} />}
      {tab === 'profiles' && <Profiles days={days} />}
      {tab === 'observations' && <Observations days={days} />}
      {tab === 'themes' && <Themes />}
      {tab === 'adherence' && <Adherence />}
    </div>
  );
}

function Loading({ error }: { error: string | null }) {
  const { t } = useT();
  return <div className="empty-state" style={error ? { color: 'var(--err)' } : undefined}>{error ?? t('common.loading')}</div>;
}

function Section({ title, help, children, wide }: { title: string; help?: string; children: React.ReactNode; wide?: boolean }) {
  return (
    <section className={`insight-section ${wide ? 'insight-section--wide' : ''}`}>
      <h2 className="pd-section-title">{title}</h2>
      {help && <div className="insight-help">{help}</div>}
      {children}
    </section>
  );
}

// ── Vue d'ensemble ───────────────────────────────────────────────────────────

interface OverviewData {
  totals: Record<string, number>;
  monthly: { month: string; entries: number; participants: number; picks: number; byCat: Record<number, number> }[];
  categories: { id: number; name: string; color: string; picks: number; participants: number }[];
  cards: { topic_id: number; name: string; color: string | null; picks: number; participants: number; contexts: number; share: number; recentShare: number; previousShare: number; trend: number }[];
}

function Overview({ days }: { days: string }) {
  const { t, lang } = useT();
  const { data, error } = useInsight<OverviewData>(`/api/insights/overview?${days}`, [days]);
  if (!data) return <Loading error={error} />;
  const T = data.totals;
  const series = data.categories.map((c) => ({ key: String(c.id), color: c.color, name: c.name }));
  const monthLabel = (m: string) => { const [y, mo] = m.split('-').map(Number); return new Date(y, mo - 1, 1).toLocaleDateString(lang === 'fr' ? 'fr-CH' : 'en-CH', { month: 'short' }); };
  const rising = data.cards.filter((c) => c.trend > 0.02 && c.recentShare >= 0.03).sort((a, b) => b.trend - a.trend).slice(0, 8);
  const falling = data.cards.filter((c) => c.trend < -0.02 && c.previousShare >= 0.03).sort((a, b) => a.trend - b.trend).slice(0, 8);
  return (
    <>
      <div className="usage-totals">
        <Kpi value={T.participants} label={t('obs.kpi.participants')} />
        <Kpi value={T.sessions} label={t('obs.kpi.sessions')} />
        <Kpi value={T.entries} label={t('obs.kpi.entries')} hint={t('obs.kpi.entriesHelp')} />
        <Kpi value={T.picks} label={t('obs.kpi.picks')} />
        <Kpi value={T.picks_per_entry} label={t('obs.kpi.picksPerEntry')} />
        <Kpi value={pct(T.entries ? T.contexts / Math.max(T.picks, 1) : null)} label={t('obs.kpi.contextRate')} />
        <Kpi value={pct(T.entries ? T.defis / T.entries : null)} label={t('obs.kpi.defiRate')} />
        <Kpi value={pct(T.entries ? T.with_letters / T.entries : null)} label={t('obs.kpi.lettersRate')} />
        <Kpi value={pct(T.takes_cards_yes + T.takes_cards_no ? T.takes_cards_yes / (T.takes_cards_yes + T.takes_cards_no) : null)} label={t('obs.kpi.takesCards')} />
        <Kpi value={pct(T.first_time_picks + T.repeat_picks ? T.repeat_picks / (T.first_time_picks + T.repeat_picks) : null)} label={t('obs.kpi.repeatRate')} hint={t('obs.kpi.repeatHelp')} />
      </div>

      <Section title={t('obs.overview.monthly')} help={t('obs.overview.monthlyHelp')} wide>
        <StackedColumns columns={data.monthly.map((m) => ({ label: monthLabel(m.month), values: Object.fromEntries(Object.entries(m.byCat).map(([k, v]) => [k, v])) }))} series={series} valueLabel={(c) => `${Object.values(c.values).reduce((a, b) => a + b, 0)} ${t('obs.cardsWord')}`} />
        <Legend items={series} />
      </Section>

      <div className="insight-grid">
        <Section title={t('obs.overview.colours')} help={t('obs.overview.coloursHelp')}>
          <BarList rows={data.categories.map((c) => ({ key: c.id, label: c.name, value: c.picks, color: c.color, sub: `${c.participants} ${t('obs.peopleShort')}` }))} />
        </Section>
        <Section title={t('obs.overview.topCards')}>
          <BarList rows={data.cards.slice(0, 18).map((c) => ({ key: c.topic_id, label: c.name, value: c.picks, color: c.color, sub: `${c.participants} ${t('obs.peopleShort')}` }))} />
        </Section>
        <Section title={t('obs.overview.rising')} help={t('obs.overview.trendHelp')}>
          {rising.length === 0 ? <div className="usage-muted">{t('obs.none')}</div> : <BarList rows={rising.map((c) => ({ key: c.topic_id, label: c.name, value: c.trend, color: c.color, sub: `${pct(c.previousShare)} → ${pct(c.recentShare)}` }))} format={(v) => `+${(v * 100).toFixed(0)} pt`} />}
        </Section>
        <Section title={t('obs.overview.falling')} help={t('obs.overview.trendHelp')}>
          {falling.length === 0 ? <div className="usage-muted">{t('obs.none')}</div> : <BarList rows={falling.map((c) => ({ key: c.topic_id, label: c.name, value: -c.trend, color: c.color, sub: `${pct(c.previousShare)} → ${pct(c.recentShare)}` }))} format={(v) => `−${(v * 100).toFixed(0)} pt`} />}
        </Section>
      </div>
    </>
  );
}

// ── Cartes ensemble ──────────────────────────────────────────────────────────

interface TogetherData {
  entries: number; nodes: { id: number; name: string; color: string | null; n: number }[];
  edges: { a: number; b: number; n: number; lift: number; expected: number }[];
  top_pairs: { a: number; b: number; n: number; lift: number; expected: number }[];
  top_lift: { a: number; b: number; n: number; lift: number; expected: number }[];
  communities: { size: number; label: string; members: { id: number; name: string; color: string | null; n: number }[]; strength: number; pairs: number }[];
  categories: { id: number; name: string; color: string }[]; colour_matrix: number[][];
}

function Together({ days }: { days: string }) {
  const { t } = useT();
  const [min, setMin] = useState(3);
  const { data, error } = useInsight<TogetherData>(`/api/insights/cooccurrence?${days}&min=${min}`, [days, min]);
  if (!data) return <Loading error={error} />;
  const name = (id: number) => data.nodes.find((n) => n.id === id)?.name ?? `#${id}`;
  const color = (id: number) => data.nodes.find((n) => n.id === id)?.color ?? null;
  const Pair = ({ p }: { p: { a: number; b: number; n: number; lift: number; expected: number } }) => (
    <tr>
      <td><i className="history-grid-row-dot" style={{ background: color(p.a) ?? 'var(--ink-3)' }} />{name(p.a)}</td>
      <td><i className="history-grid-row-dot" style={{ background: color(p.b) ?? 'var(--ink-3)' }} />{name(p.b)}</td>
      <td className="usage-num">{p.n}</td>
      <td className="usage-num usage-muted">{p.expected}</td>
      <td className={`usage-num ${p.lift >= 1.5 ? 'insight-strong' : ''}`}>×{p.lift}</td>
    </tr>
  );
  return (
    <>
      <div className="insight-toolbar">
        <span className="usage-muted">{t('obs.together.entries').replace('{n}', String(data.entries))}</span>
        <label className="psearch-toggle">{t('obs.together.minSupport')} <select className="field-input usage-sort" value={min} onChange={(e) => setMin(Number(e.target.value))}>{[2, 3, 4, 5, 8].map((v) => <option key={v} value={v}>{v}</option>)}</select></label>
      </div>
      <Section title={t('obs.together.graph')} help={t('obs.together.graphHelp')} wide>
        {data.nodes.length === 0 ? <div className="usage-muted">{t('obs.none')}</div> : <ForceGraph nodes={data.nodes} edges={data.edges.filter((e) => e.lift >= 1)} />}
      </Section>
      <div className="insight-grid">
        <Section title={t('obs.together.communities')} help={t('obs.together.communitiesHelp')}>
          {data.communities.map((c, i) => (
            <div key={i} className="community">
              <div className="community-head"><strong>{c.label}</strong><span className="usage-muted"> · {c.size} {t('obs.cardsWord')} · lift ×{c.strength}</span></div>
              <div className="community-members">{c.members.map((m) => <span key={m.id} className="chip"><i style={{ background: m.color ?? 'var(--ink-3)' }} />{m.name} <small>{m.n}</small></span>)}</div>
            </div>
          ))}
          {data.communities.length === 0 && <div className="usage-muted">{t('obs.none')}</div>}
        </Section>
        <Section title={t('obs.together.colours')} help={t('obs.together.coloursHelp')}>
          <Heatmap rows={data.categories.map((c) => c.name)} cols={data.categories.map((c) => c.name)} matrix={data.colour_matrix} colors={data.categories.map((c) => c.color)} />
        </Section>
        <Section title={t('obs.together.pairs')} help={t('obs.together.pairsHelp')}>
          <table className="usage-table"><thead><tr><th>{t('obs.card')}</th><th>{t('obs.card')}</th><th className="usage-num">{t('obs.together.n')}</th><th className="usage-num">{t('obs.together.expected')}</th><th className="usage-num">lift</th></tr></thead>
            <tbody>{data.top_pairs.slice(0, 20).map((p) => <Pair key={`${p.a}-${p.b}`} p={p} />)}</tbody></table>
        </Section>
        <Section title={t('obs.together.lift')} help={t('obs.together.liftHelp')}>
          <table className="usage-table"><thead><tr><th>{t('obs.card')}</th><th>{t('obs.card')}</th><th className="usage-num">{t('obs.together.n')}</th><th className="usage-num">{t('obs.together.expected')}</th><th className="usage-num">lift</th></tr></thead>
            <tbody>{data.top_lift.slice(0, 20).map((p) => <Pair key={`${p.a}-${p.b}`} p={p} />)}</tbody></table>
        </Section>
      </div>
    </>
  );
}

// ── Parcours ─────────────────────────────────────────────────────────────────

interface JourneysData {
  participants: number; participants_3plus: number; steps: number; mean_length: number; length_hist: Record<string, number>;
  transitions: { from_name: string; to_name: string; from_color: string | null; to_color: string | null; n: number; lift: number }[];
  surprising: { from_name: string; to_name: string; from_color: string | null; to_color: string | null; n: number; lift: number }[];
  colour_transitions: number[][]; categories: { id: number; name: string; color: string }[];
  phases: { phase: string; picks: number; byCat: Record<number, number>; byHorizon: Record<string, number>; repeats: number; participants: number }[];
  colour_position: { id: number; name: string; color: string; n: number; mean_position: number }[];
  card_position: { early: { name: string; color: string | null; n: number; mean_position: number }[]; late: { name: string; color: string | null; n: number; mean_position: number }[] };
}

function Journeys({ days }: { days: string }) {
  const { t } = useT();
  const { data, error } = useInsight<JourneysData>(`/api/insights/journeys?${days}`, [days]);
  if (!data) return <Loading error={error} />;
  const series = data.categories.map((c) => ({ key: String(c.id), color: c.color, name: c.name }));
  const Trans = ({ rows }: { rows: JourneysData['transitions'] }) => (
    <table className="usage-table"><thead><tr><th>{t('obs.journeys.from')}</th><th>{t('obs.journeys.to')}</th><th className="usage-num">n</th><th className="usage-num">lift</th></tr></thead>
      <tbody>{rows.map((r, i) => (
        <tr key={i}><td><i className="history-grid-row-dot" style={{ background: r.from_color ?? 'var(--ink-3)' }} />{r.from_name}</td><td><i className="history-grid-row-dot" style={{ background: r.to_color ?? 'var(--ink-3)' }} />{r.to_name}</td><td className="usage-num">{r.n}</td><td className={`usage-num ${r.lift >= 1.5 ? 'insight-strong' : ''}`}>×{r.lift}</td></tr>
      ))}</tbody></table>
  );
  return (
    <>
      <div className="usage-totals">
        <Kpi value={data.participants} label={t('obs.kpi.participants')} />
        <Kpi value={data.participants_3plus} label={t('obs.journeys.threePlus')} />
        <Kpi value={data.mean_length} label={t('obs.journeys.meanLength')} />
        <Kpi value={data.steps} label={t('obs.journeys.steps')} hint={t('obs.journeys.stepsHelp')} />
      </div>
      <div className="insight-grid">
        <Section title={t('obs.journeys.phases')} help={t('obs.journeys.phasesHelp')}>
          <StackedColumns columns={data.phases.map((p) => ({ label: t(`obs.phase.${p.phase}`), values: Object.fromEntries(Object.entries(p.byCat).map(([k, v]) => [k, v / Math.max(p.picks, 1)])) }))} series={series} height={200} valueLabel={(c) => `${Math.round(Object.values(c.values).reduce((a, b) => a + b, 0) * 100)} %`} />
          <Legend items={series} />
          <div className="usage-muted" style={{ marginTop: 6 }}>
            {data.phases.map((p) => `${t(`obs.phase.${p.phase}`)} : ${p.picks} ${t('obs.cardsWord')}, ${pct(p.picks ? p.repeats / p.picks : null)} ${t('obs.journeys.repeats')}`).join(' · ')}
          </div>
        </Section>
        <Section title={t('obs.journeys.position')} help={t('obs.journeys.positionHelp')}>
          <Dots rows={data.colour_position.map((c) => ({ label: c.name, value: c.mean_position, color: c.color, n: c.n }))} leftLabel={t('obs.phase.debut')} rightLabel={t('obs.phase.fin')} />
          <div className="insight-two">
            <div><div className="insight-sub">{t('obs.journeys.early')}</div><BarList rows={data.card_position.early.map((c) => ({ key: c.name, label: c.name, value: 1 - c.mean_position, color: c.color, sub: `n=${c.n}` }))} max={1} format={(v) => `${Math.round((1 - v) * 100)}`} /></div>
            <div><div className="insight-sub">{t('obs.journeys.late')}</div><BarList rows={data.card_position.late.map((c) => ({ key: c.name, label: c.name, value: c.mean_position, color: c.color, sub: `n=${c.n}` }))} max={1} format={(v) => `${Math.round(v * 100)}`} /></div>
          </div>
        </Section>
        <Section title={t('obs.journeys.lengths')} help={t('obs.journeys.lengthsHelp')}>
          <BarList rows={Object.entries(data.length_hist).map(([k, v]) => ({ key: k, label: `${k} ${t('obs.seancesWord')}`, value: v }))} />
        </Section>
        <Section title={t('obs.journeys.colourTransitions')} help={t('obs.journeys.colourTransitionsHelp')}>
          <Heatmap rows={data.categories.map((c) => c.name)} cols={data.categories.map((c) => c.name)} matrix={data.colour_transitions} rowNormalise colors={data.categories.map((c) => c.color)} />
        </Section>
        <Section title={t('obs.journeys.transitions')} help={t('obs.journeys.transitionsHelp')}><Trans rows={data.transitions.slice(0, 20)} /></Section>
        <Section title={t('obs.journeys.surprising')} help={t('obs.journeys.surprisingHelp')}><Trans rows={data.surprising} /></Section>
      </div>
    </>
  );
}

// ── Profils ──────────────────────────────────────────────────────────────────

interface ProfilesData {
  participants: number; k?: number; note?: string;
  clusters: { id: number; size: number; label: string; shares: { category_id: number; name: string; color: string; share: number }[]; top_cards: { topic_id: number; name: string; n: number; share: number; lift: number }[]; mean_entries: number; presence_rate: number | null; deltas: Record<string, { n: number; mean: number | null; improved: number }>; members: { key: string; v2_participant_id: number | null; name: string; entries: number; top: string[] }[] }[];
}

function Profiles({ days }: { days: string }) {
  const { t, lang } = useT();
  const dl = lang as DimLang;
  const [k, setK] = useState(5);
  const [open, setOpen] = useState<number | null>(null);
  const { data, error } = useInsight<ProfilesData>(`/api/insights/profiles?${days}&k=${k}`, [days, k]);
  if (!data) return <Loading error={error} />;
  return (
    <>
      <div className="insight-toolbar">
        <span className="usage-muted">{t('obs.profiles.intro').replace('{n}', String(data.participants))}</span>
        <label className="psearch-toggle">{t('obs.profiles.k')} <select className="field-input usage-sort" value={k} onChange={(e) => setK(Number(e.target.value))}>{[3, 4, 5, 6, 7].map((v) => <option key={v} value={v}>{v}</option>)}</select></label>
      </div>
      {data.note && <div className="usage-muted">{t('obs.profiles.notEnough')}</div>}
      <div className="insight-grid">
        {data.clusters.map((c) => (
          <section key={c.id} className="insight-section cluster">
            <div className="cluster-head">
              <h2 className="pd-section-title" style={{ margin: 0 }}>{c.label}</h2>
              <span className="badge badge-accent">{c.size} {t('obs.peopleShort')}</span>
            </div>
            <div className="cluster-shares">{c.shares.map((s) => <span key={s.category_id} style={{ width: `${s.share * 100}%`, background: s.color }} title={`${s.name} ${pct(s.share)}`} />)}</div>
            <div className="usage-muted">{t('obs.profiles.meanEntries')} {c.mean_entries} · {t('obs.profiles.presence')} {pct(c.presence_rate)}</div>
            <div className="insight-sub">{t('obs.profiles.topCards')}</div>
            <BarList rows={c.top_cards.map((x) => ({ key: x.topic_id, label: x.name, value: x.share, sub: `×${x.lift}` }))} max={1} format={(v) => pct(v)} />
            <div className="insight-sub">{t('obs.profiles.deltas')}</div>
            <div className="cluster-deltas">
              {SCORE_DIMENSIONS.map((d) => { const x = c.deltas[d.key]; return <span key={d.key} title={d.label[dl]}>{d.reportLabel[dl]} : {x.mean == null ? '—' : `${x.mean > 0 ? '+' : ''}${x.mean}`} <small>({x.improved}/{x.n} ↑)</small></span>; })}
            </div>
            <button className="btn btn-ghost btn-sm" onClick={() => setOpen(open === c.id ? null : c.id)}>{open === c.id ? t('common.showLess') : `${t('obs.profiles.members')} (${c.size})`}</button>
            {open === c.id && (
              <ul className="pt-list cluster-members">{c.members.map((m) => <li key={m.key}>{m.v2_participant_id ? <a href={`#participant/v2/${m.v2_participant_id}`}>{m.name}</a> : m.name} <span className="usage-muted">· {m.entries} {t('obs.seancesWord')} · {m.top.join(', ')}</span></li>)}</ul>
            )}
          </section>
        ))}
      </div>
    </>
  );
}

// ── Observations ─────────────────────────────────────────────────────────────

interface ObservationsData {
  participants: number; entries_with_letters: number;
  by_index: { key: string; label: string; points: { index: number; n: number; mean: number | null }[] }[];
  takes_cards_by_index: { index: number; n: number; rate: number | null }[];
  deltas: Record<string, { n: number; improved: number; same: number; declined: number; mean: number | null }>;
  associations: { positive: Assoc[]; negative: Assoc[] };
}
interface Assoc { topic_id: number; name: string; color: string | null; takers: number; dims: Record<string, { takers_mean: number | null; others_mean: number | null; diff: number | null; n: number }> }

function Observations({ days }: { days: string }) {
  const { t, lang } = useT();
  const dl = lang as DimLang;
  const { data, error } = useInsight<ObservationsData>(`/api/insights/observations?${days}`, [days]);
  if (!data) return <Loading error={error} />;
  const AssocTable = ({ rows }: { rows: Assoc[] }) => (
    <table className="usage-table"><thead><tr><th>{t('obs.card')}</th><th className="usage-num">{t('obs.obs.takers')}</th>{SCORE_DIMENSIONS.map((d) => <th key={d.key} className="usage-num" title={d.label[dl]}>{d.reportLabel[dl]}</th>)}</tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r.topic_id}><td><i className="history-grid-row-dot" style={{ background: r.color ?? 'var(--ink-3)' }} />{r.name}</td><td className="usage-num">{r.takers}</td>
          {SCORE_DIMENSIONS.map((d) => { const x = r.dims[d.key]; return <td key={d.key} className={`usage-num ${x.diff != null && Math.abs(x.diff) >= 0.4 ? 'insight-strong' : ''}`} title={x.takers_mean != null ? `${t('obs.obs.takers')} ${x.takers_mean} · ${t('obs.obs.others')} ${x.others_mean}` : ''}>{x.diff == null ? '—' : `${x.diff > 0 ? '+' : ''}${x.diff}`}</td>; })}
        </tr>
      ))}</tbody></table>
  );
  return (
    <>
      <div className="usage-totals">
        <Kpi value={data.participants} label={t('obs.kpi.participants')} />
        <Kpi value={data.entries_with_letters} label={t('obs.obs.withLetters')} />
        {SCORE_DIMENSIONS.map((d) => { const x = data.deltas[d.key]; return <Kpi key={d.key} value={x.n ? `${x.improved} ↑ ${x.same} = ${x.declined} ↓` : '—'} label={d.reportLabel[dl]} hint={`${t('obs.obs.deltaHelp')} n=${x.n}, ${t('obs.obs.mean')} ${x.mean ?? '—'}`} />; })}
      </div>
      <div className="insight-grid">
        <Section title={t('obs.obs.byIndex')} help={t('obs.obs.byIndexHelp')}>
          <Lines series={data.by_index.map((d, i) => ({ name: d.label, color: DIM_COLORS[i], points: d.points.map((p) => ({ x: p.index - 1, y: p.mean, n: p.n })) }))} xLabels={data.by_index[0].points.map((p) => String(p.index))} yTicks={LETTERS.map((L, i) => ({ v: i + 1, label: L }))} />
          <Legend items={data.by_index.map((d, i) => ({ name: d.label, color: DIM_COLORS[i] }))} />
        </Section>
        <Section title={t('obs.obs.takesByIndex')} help={t('obs.obs.takesByIndexHelp')}>
          <Lines series={[{ name: t('obs.kpi.takesCards'), color: 'var(--accent)', points: data.takes_cards_by_index.map((p) => ({ x: p.index - 1, y: p.rate, n: p.n })) }]} xLabels={data.takes_cards_by_index.map((p) => String(p.index))} yMin={0} yMax={1} yTicks={[0, 0.25, 0.5, 0.75, 1].map((v) => ({ v, label: `${v * 100}%` }))} height={150} />
        </Section>
        <Section title={t('obs.obs.assocPos')} help={t('obs.obs.assocHelp')}><AssocTable rows={data.associations.positive} /></Section>
        <Section title={t('obs.obs.assocNeg')} help={t('obs.obs.assocHelp')}><AssocTable rows={data.associations.negative} /></Section>
      </div>
    </>
  );
}

// ── Thèmes émergents ─────────────────────────────────────────────────────────

interface ThemesData {
  ai: boolean;
  run: null | { status: string; progress: string | null; error: string | null; computed_at: string | null; started_at: string | null; params: { days: number | null } | null; payload: null | {
    window_days: number | null; texts: number; participants: number; unassigned: number; model: string;
    themes: { id: number; label: string; description: string; signals: string[]; texts: number; participants: number; share: number; top_cards: { name: string; n: number }[]; by_quarter: { quarter: string; n: number }[]; examples: { text: string; card: string | null; date: string }[] }[];
    theme_pairs: { a_label: string; b_label: string; n: number }[]; readings: { title: string; text: string }[];
  } };
}

function Themes() {
  const { t, lang } = useT();
  const [period, setPeriod] = useState<string>('all');
  const { data, error, reload } = useInsight<ThemesData>('/api/insights/themes', []);
  const [open, setOpen] = useState<number | null>(null);
  useEffect(() => {
    if (data?.run?.status === 'running') { const id = setTimeout(reload, 4000); return () => clearTimeout(id); }
  }, [data]);  // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <Loading error={error} />;
  const run = data.run;
  const P = run?.payload ?? null;
  const start = async () => { await api.post('/api/insights/themes/recompute', { days: period === 'all' ? null : Number(period) }).catch(() => {}); reload(); };
  const fmt = (iso: string) => new Date(iso).toLocaleString(lang === 'fr' ? 'fr-CH' : 'en-CH', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  return (
    <>
      <div className="insight-toolbar">
        <div className="usage-muted" style={{ maxWidth: 720 }}>{t('obs.themes.intro')}</div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select className="field-input usage-sort" value={period} onChange={(e) => setPeriod(e.target.value)}>{PERIODS.map((p) => <option key={p.key} value={p.key}>{t(`obs.period.${p.key}`)}</option>)}</select>
          <button className="btn btn-accent btn-sm" disabled={!data.ai || run?.status === 'running'} onClick={start} title={!data.ai ? t('obs.themes.aiOff') : ''}><RefreshCw size={13} /> {t('obs.themes.recompute')}</button>
        </div>
      </div>
      {!data.ai && <div className="pt-v2-warning">{t('obs.themes.aiOff')}</div>}
      {run?.status === 'running' && <div className="evo-note evo-note--pending">{t('obs.themes.running')} {run.progress ?? ''}</div>}
      {run?.status === 'error' && <div className="evo-note" style={{ color: 'var(--err)' }}>{t('obs.themes.error')} {run.error}</div>}
      {!P && run?.status !== 'running' && <div className="empty-state">{t('obs.themes.none')}</div>}
      {P && (
        <>
          <div className="usage-muted" style={{ margin: '4px 0 12px' }}>
            {t('obs.themes.computed')} {run?.computed_at ? fmt(run.computed_at) : ''} · {P.texts} {t('obs.themes.texts')} · {P.participants} {t('obs.peopleShort')} · {P.unassigned} {t('obs.themes.unassigned')} · {P.model}
          </div>
          {P.readings.length > 0 && (
            <Section title={t('obs.themes.readings')} help={t('obs.themes.readingsHelp')} wide>
              <div className="readings">{P.readings.map((r, i) => <div key={i} className="reading"><strong>{r.title}</strong><p>{r.text}</p></div>)}</div>
            </Section>
          )}
          <Section title={t('obs.themes.grid')} help={t('obs.themes.gridHelp')} wide>
            <div className="themes">
              {P.themes.map((th) => (
                <div key={th.id} className="theme">
                  <div className="theme-head">
                    <strong>{th.label}</strong>
                    <span className="usage-muted">{th.texts} {t('obs.themes.texts')} · {th.participants} {t('obs.peopleShort')} · {pct(th.share)}</span>
                  </div>
                  <div className="barlist-track theme-bar"><span className="barlist-bar" style={{ width: `${Math.max(2, th.share * 100 * 2)}%` }} /></div>
                  <div className="theme-desc">{th.description}</div>
                  <div className="theme-cards">{th.top_cards.map((c) => <span key={c.name} className="chip">{c.name} <small>{c.n}</small></span>)}</div>
                  <div className="theme-quarters">{th.by_quarter.map((q) => <span key={q.quarter} title={`${q.quarter} : ${q.n}`} style={{ height: `${Math.max(2, (q.n / Math.max(1, ...th.by_quarter.map((x) => x.n))) * 100)}%` }} />)}</div>
                  <button className="btn btn-ghost btn-sm" onClick={() => setOpen(open === th.id ? null : th.id)}>{open === th.id ? t('common.showLess') : t('obs.themes.examples')}</button>
                  {open === th.id && <ul className="pt-list theme-examples">{th.examples.map((e, i) => <li key={i}>« {e.text} »{e.card && <span className="usage-muted"> — {e.card}</span>}</li>)}</ul>}
                </div>
              ))}
            </div>
          </Section>
          <Section title={t('obs.themes.pairs')} help={t('obs.themes.pairsHelp')}>
            <BarList rows={P.theme_pairs.map((p, i) => ({ key: i, label: `${p.a_label} + ${p.b_label}`, value: p.n }))} />
          </Section>
        </>
      )}
    </>
  );
}

// ── Adhésion ─────────────────────────────────────────────────────────────────

interface AdherenceData {
  window_days: number; weeks: string[];
  headline: { intervenants_with_seances: number; using: number; not_using: number; held: number; recorded: number; adherence: number | null; planned_participants: number; participants_with_entries: number; entries: number; complete_rate: number | null; context_rate: number | null; bsp_recorded: number; adhoc: number; v21_available: boolean };
  weekly: { week: string; held: number; recorded: number }[];
  intervenants: { staff_id: number | null; user_id: number | null; name: string; held: number; opened: number; recorded: number; adherence: number | null; entries: number; planned: number; picks_per_entry: number | null; cards_rate: number | null; context_rate: number | null; defi_rate: number | null; letters_rate: number | null; complete_rate: number | null; last_recorded: string | null; lag_median_days: number | null; status: string; locations: string[]; adhoc: number; bsp: number; weeks: { week: string; held: number; recorded: number }[] }[];
  locations: { name: string; held: number; recorded: number; adherence: number | null }[];
}

function Adherence() {
  const { t, lang } = useT();
  const [days, setDays] = useState(90);
  const { data, error } = useInsight<AdherenceData>(`/api/insights/adherence?days=${days}`, [days]);
  if (!data) return <Loading error={error} />;
  const H = data.headline;
  const fmtDate = (iso: string | null) => iso ? new Date(iso + 'T00:00:00').toLocaleDateString(lang === 'fr' ? 'fr-CH' : 'en-CH', { day: 'numeric', month: 'short' }) : '—';
  const weekLabel = (w: string) => new Date(w + 'T00:00:00').toLocaleDateString(lang === 'fr' ? 'fr-CH' : 'en-CH', { day: 'numeric', month: 'short' });
  const Rate = ({ v }: { v: number | null }) => <td className={`usage-num ${v == null ? 'usage-muted' : v >= 0.7 ? 'rate-good' : v >= 0.3 ? 'rate-mid' : 'rate-low'}`}>{pct(v)}</td>;
  return (
    <>
      <div className="insight-toolbar">
        <div className="scope-toggle" role="radiogroup" aria-label={t('obs.period')}>
          {[30, 90, 180, 365].map((d) => <button key={d} type="button" role="radio" aria-checked={days === d} className={`scope-toggle-btn ${days === d ? 'scope-toggle-btn--active' : ''}`} onClick={() => setDays(d)}>{d} {t('obs.daysWord')}</button>)}
        </div>
        {!H.v21_available && <span className="pt-v2-warning">{t('obs.adh.noEveil')}</span>}
      </div>
      <div className="usage-totals">
        <Kpi value={H.intervenants_with_seances} label={t('obs.adh.withSeances')} />
        <Kpi value={H.using} label={t('obs.adh.using')} tone="ok" />
        <Kpi value={H.not_using} label={t('obs.adh.notUsing')} tone={H.not_using > 0 ? 'err' : undefined} />
        <Kpi value={pct(H.adherence)} label={t('obs.adh.adherence')} hint={`${H.recorded} / ${H.held}`} tone={H.adherence == null ? undefined : H.adherence >= 0.7 ? 'ok' : H.adherence >= 0.4 ? 'warn' : 'err'} />
        <Kpi value={`${H.recorded} / ${H.held}`} label={t('obs.adh.heldRecorded')} />
        <Kpi value={`${H.participants_with_entries} / ${H.planned_participants}`} label={t('obs.adh.participants')} hint={t('obs.adh.participantsHelp')} />
        <Kpi value={H.entries} label={t('obs.kpi.entries')} />
        <Kpi value={pct(H.complete_rate)} label={t('obs.adh.completeRate')} />
        <Kpi value={pct(H.context_rate)} label={t('obs.kpi.contextRate')} />
        <Kpi value={`${H.bsp_recorded} · ${H.adhoc}`} label={t('obs.adh.bspAdhoc')} />
      </div>
      <Section title={t('obs.adh.weekly')} help={t('obs.adh.weeklyHelp')} wide>
        <StackedColumns columns={data.weekly.map((w) => ({ label: weekLabel(w.week), values: { recorded: w.recorded, missing: Math.max(0, w.held - w.recorded) } }))} series={[{ key: 'recorded', color: 'var(--accent)', name: t('obs.adh.recorded') }, { key: 'missing', color: 'var(--line-strong)', name: t('obs.adh.notRecorded') }]} height={140} labelEvery={Math.max(1, Math.ceil(data.weekly.length / 12))} valueLabel={(c) => `${c.values.recorded}/${c.values.recorded + c.values.missing}`} />
        <Legend items={[{ color: 'var(--accent)', name: t('obs.adh.recorded') }, { color: 'var(--line-strong)', name: t('obs.adh.notRecorded') }]} />
      </Section>
      <Section title={t('obs.adh.table')} help={t('obs.adh.tableHelp')} wide>
        <div style={{ overflowX: 'auto' }}>
          <table className="usage-table adh-table">
            <thead><tr>
              <th>{t('obs.adh.intervenant')}</th><th></th><th className="usage-num">{t('obs.adh.held')}</th><th className="usage-num">{t('obs.adh.recordedShort')}</th><th className="usage-num">{t('obs.adh.adherence')}</th><th>{t('obs.adh.weeks')}</th>
              <th className="usage-num">{t('obs.kpi.entries')}</th><th className="usage-num">{t('obs.adh.cards')}</th><th className="usage-num">{t('obs.adh.context')}</th><th className="usage-num">{t('obs.adh.defi')}</th><th className="usage-num">{t('obs.adh.letters')}</th><th className="usage-num">{t('obs.adh.complete')}</th><th className="usage-num" title={t('obs.adh.lagHelp')}>{t('obs.adh.lag')}</th><th>{t('obs.adh.last')}</th><th>{t('obs.adh.locations')}</th>
            </tr></thead>
            <tbody>
              {data.intervenants.map((r) => (
                <tr key={`${r.staff_id}-${r.user_id}`} className={`adh-row--${r.status}`}>
                  <td className="adh-name">{r.name}{(r.bsp > 0 || r.adhoc > 0) && <span className="usage-muted"> · {r.bsp} BSP · {r.adhoc} ad hoc</span>}</td>
                  <td><span className={`chip chip--${r.status}`}>{t(`obs.status.${r.status}`)}</span></td>
                  <td className="usage-num">{r.held}</td>
                  <td className="usage-num">{r.recorded}{r.opened > r.recorded && <span className="usage-muted" title={t('obs.adh.openedOnly')}> (+{r.opened - r.recorded})</span>}</td>
                  <td className="usage-num"><span className="adh-bar"><span style={{ width: `${(r.adherence ?? 0) * 100}%` }} /></span>{pct(r.adherence)}</td>
                  <td className="adh-spark"><Spark points={r.weeks} /></td>
                  <td className="usage-num">{r.entries}</td>
                  <Rate v={r.cards_rate} /><Rate v={r.context_rate} /><Rate v={r.defi_rate} /><Rate v={r.letters_rate} /><Rate v={r.complete_rate} />
                  <td className="usage-num">{r.lag_median_days == null ? '—' : `${r.lag_median_days} j`}</td>
                  <td>{fmtDate(r.last_recorded)}</td>
                  <td className="usage-muted">{r.locations.join(', ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      <Section title={t('obs.adh.byLocation')}>
        <BarList rows={data.locations.map((l) => ({ key: l.name, label: l.name, value: l.adherence ?? 0, sub: `${l.recorded}/${l.held}` }))} max={1} format={(v) => pct(v)} />
      </Section>
    </>
  );
}

function QualityReport({days}:{days:string}) {
  const {data,error}=useInsight<{planned:number;held:number;recorded_sessions:number;complete_entries:number;entries:number;journal_entries:number;manual_entries:number;not_planned:number;base_offered_sessions:number;groups:{cohort:string;deck:string;entries:number;cards:[string,number][]}[]}>(`/api/quality/report?${days}`,[days]);
  if(!data)return <Loading error={error} />;
  return <section><h2>Lecture des données</h2>
    <p>Une séance planifiée n’est pas forcément tenue. « Tenue » signifie qu’au moins une présence est confirmée dans Éveil. Les saisies comprennent aussi les BSP et les séances ajoutées manuellement.</p>
    <table className="table"><tbody>{[
      ['Séances EE planifiées sur la période',data.planned],['Séances EE avec présence confirmée',data.held],
      ['Séances avec une saisie dans Evolution',data.recorded_sessions],['Fiches complètes',`${data.complete_entries} / ${data.entries}`],
      ['Reprises du journal (hors présence et complétude)',data.journal_entries],['Ajouts manuels',data.manual_entries],['Entrées écartées du planning (toutes dates)',data.not_planned],['Séances où tout le jeu de base a été proposé',data.base_offered_sessions],
    ].map(([label,n])=><tr key={label}><th>{label}</th><td>{n}</td></tr>)}</tbody></table>
    <h3>Cartes par mesure et composition</h3><p>La mesure est celle de la date de la séance. Les situations non déterminées restent visibles. Le nombre de choix d’une carte ne mesure pas l’atteinte d’un objectif.</p>
    <div className="objective-table-wrap"><table className="table"><thead><tr><th>Mesure</th><th>Composition / jeu</th><th>Fiches</th><th>Cartes choisies</th></tr></thead><tbody>
      {data.groups.map(g=><tr key={g.cohort+g.deck}><td>{g.cohort}</td><td>{g.deck}</td><td>{g.entries}</td><td><details><summary>{g.cards.length} cartes distinctes</summary>{g.cards.map(([name,n])=><div key={name}>{name} : {n}</div>)}</details></td></tr>)}
    </tbody></table></div>
  </section>;
}
